/**
 * Code that dispatches actions from inside the autosave middleware's save
 * pipeline (i.e. `doSaveConfiguration`) should attach this meta via
 * `withAutosaveDrivenMeta` so the preview-sync trigger can suppress redundant
 * flushes — the originating IMMEDIATE/DELAYED action that kicked off the save
 * already scheduled one.
 */
export interface AutosaveDrivenMeta {
  autosaveDriven?: boolean;
}

/**
 * Returns true if the action is driven (dispatched) by the autosave middleware
 */
export function isAutosaveDriven(action: unknown): boolean {
  if (!action || typeof action !== 'object') {
    return false;
  }

  return !!(action as { meta?: AutosaveDrivenMeta }).meta?.autosaveDriven;
}

/**
 * Returns a new action with metadata indicating the action is driven by the autosave
 * middleware. Any existing `meta` fields on the action are preserved.
 * Inverse of `isAutosaveDriven`.
 */
export function withAutosaveDrivenMeta<A extends object>(
  action: A,
): A & { meta: AutosaveDrivenMeta } {
  const existingMeta = (action as A & { meta?: AutosaveDrivenMeta }).meta;
  return {
    ...action,
    meta: { ...existingMeta, autosaveDriven: true },
  };
}
